import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, Plus, FileText } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BATCH_STATUS_META, money, fmtDate } from '@/lib/accountingConstants';
import EmptyState from '@/components/accounting/EmptyState';
import TableSkeleton from '@/components/accounting/TableSkeleton';
import PreviewBatchDialog from '@/components/accounting/PreviewBatchDialog';

const FILTERS = [null, 'draft', 'pending_approval', 'approved', 'ready_for_payment', 'paid', 'completed_with_exceptions', 'rejected', 'cancelled'];

export default function PaymentBatchesListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  // Phase 27.30.4 — deep-link entry from a Carrier Import can arrive
  // here with a pre-selected list of statement_ids that must scope the
  // preview + create call.
  const [preselect, setPreselect] = useState(null);

  useEffect(() => {
    const st = location.state;
    if (st?.preselectStatementIds?.length) {
      setPreselect({ ids: st.preselectStatementIds, label: st.importLabel });
      setShowPreview(true);
      // Clear the router state so a page refresh doesn't reopen it.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status) params.status = status;
      const { data } = await accountingAPI.listBatches(params);
      setBatches(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load batches');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="payment-batches-page">
      <PageHeader
        title="Payment Batches"
        description="Group approved statements into a payment run. Draft → Pending Approval → Approved → Ready for Payment."
        breadcrumbs={[{ label: 'Accounting', to: '/accounting' }, { label: 'Payment Batches' }]}
        actions={
          <Button onClick={() => setShowPreview(true)} data-testid="new-batch-btn">
            <Plus className="h-4 w-4 mr-2" /> New Batch
          </Button>
        }
      />

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const isActive = f === status;
          const label = f ? (BATCH_STATUS_META[f]?.label || f) : 'All';
          return (
            <button
              key={f || 'all'}
              onClick={() => setStatus(f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                isActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`batch-filter-${f || 'all'}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Batches table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={5} cols={8} />
          ) : batches.length === 0 ? (
            <EmptyState
              icon={Package}
              title={status ? `No batches with status "${BATCH_STATUS_META[status]?.label}"` : 'No Payment Batches Yet'}
              description="Group approved statements into a payment run. Click New Batch to preview eligible recipients."
              action={<Button size="sm" onClick={() => setShowPreview(true)}><Plus className="h-4 w-4 mr-2" /> New Batch</Button>}
              dataTestId="batches-empty-state"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Batch #</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Recipients</th>
                    <th className="text-right px-4 py-3 font-medium">Positive</th>
                    <th className="text-right px-4 py-3 font-medium">Offset</th>
                    <th className="text-right px-4 py-3 font-medium">Net Payable</th>
                    <th className="text-left px-4 py-3 font-medium">Created</th>
                    <th className="text-left px-4 py-3 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => {
                    const meta = BATCH_STATUS_META[b.status] || { label: b.status, tone: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText };
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={b.batch_id}
                        onClick={() => navigate(`/accounting/payment-batches/${b.batch_id}`)}
                        className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`batch-row-${b.batch_id}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-slate-800">{b.batch_number}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${meta.tone} border font-normal`}>
                            <Icon className="h-3 w-3 mr-1 inline" /> {meta.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">{b.recipient_count}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">{money(b.total_positive)}</td>
                        <td className="px-4 py-3 text-right text-orange-600">{b.total_offset > 0 ? `-${money(b.total_offset)}` : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium">{money(b.total_net_payable)}</td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(b.created_at)}</td>
                        <td className="px-4 py-3 text-slate-600">{b.created_by_name || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PreviewBatchDialog
        open={showPreview}
        preselectStatementIds={preselect?.ids || null}
        preselectLabel={preselect?.label || null}
        onClose={() => { setShowPreview(false); setPreselect(null); }}
        onCreated={(batch) => {
          setShowPreview(false);
          setPreselect(null);
          toast.success(`Batch Created — ${batch.batch_number}`);
          navigate(`/accounting/payment-batches/${batch.batch_id}`);
        }}
      />
    </div>
  );
}
